import { Navigate, Outlet, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { JournalPage } from './pages/journal/JournalPage';
import { TrialHomePage } from './pages/trial/TrialHomePage';
import { TrialSetupPage } from './pages/trial/TrialSetupPage';
import { TrialMobilePage } from './pages/trial/TrialMobilePage';
import { TrialLivePage } from './pages/demo/TrialLivePage.js';
import { Profile } from './pages/settings/profile/Profile';
import { CoreLayout } from './components/layout/CoreLayout.js';
import { TrialLayout } from './components/layout/TrialLayout.js';
import { SalesPage } from './pages/sales/SalesPage.js';
import { ProtectedRoute } from './components/routes/ProtectedRoute.js';
import { ResetPasswordPage } from './pages/reset-password/ResetPasswordPage.js';
import Issue from './assets/images/issue.jpg';
import { OperatorPage } from './pages/operator/OperatorPage.js';
import { OperatorWorkstationPage } from './pages/operator/OperatorWorkstationPage.js';
import { isMobileDevice } from './helpers/common/isMobileDevice.js';
import { SetupPage } from './pages/setup/SetupPage.js';
import { SpecificationPage } from './pages/setup/SpecificationPage.js';
import { CalibrationPage } from './pages/calibration/CalibrationPage.js';
import { ErgonomicsPage } from './pages/ergonomics/ErgonomicsPage';
import { ErgonomicsLivePage } from './pages/ergonomics/ErgonomicsLivePage';
import { ProcessesPage } from "./pages/processes/ProcessesPage";
import { ProcessPage } from "./pages/processes/ProcessPage";
import { NewProcessPage } from "./pages/processes/NewProcessPage";
import { ProcessLayout } from "./components/layout/ProcessLayout";
import { TAGLivePage } from "./pages/live-interface/TAGLivePage";
import { TAGUSLivePage } from "./pages/live-interface/TAGUSLivePage";
import { LCALivePage } from "./pages/live-interface/LCALivePage";
import { HermesLivePage } from "./pages/live-interface/HermesLivePage";
import { LISILivePage } from "./pages/live-interface/LISILivePage";
import { HermesUSLivePage } from "./pages/live-interface/HermesUSLivePage";
import { RenaultLivePage } from "./pages/live-interface/RenaultLivePage";
import { EDFLivePage } from "./pages/live-interface/EDFLivePage";
import { QualityPage } from "./pages/quality/QualityPage";
import { SnapshotsGalleryPage } from "./pages/gallery/snapshots/SnapshotsGalleryPage";
import { EventScoresGalleryPage } from "./pages/gallery/event-scores/EventScoresGalleryPage";
import { ShortsPage } from "./pages/shorts/ShortsPage";
import { DevicesSettingsPage } from "./pages/settings/devices/DevicesSettingsPage";
import { DiorLivePage } from "./pages/live-interface/DiorLivePage";
import { AuditPage } from "./pages/audit/AuditPage";
import { AuditSummaryPage } from "./pages/audit/AuditSummaryPage";
import { RecorderPage } from "./pages/recorder/RecorderPage";
import { RecorderMeetPage } from "./pages/recorder/RecorderMeetPage";
import { ArquusLivePage } from "./pages/live-interface/ArquusLivePage";
import { DemoLivePage } from "./pages/live-interface/DemoLivePage";
import { DemoHomePage } from "./pages/demo/DemoHomePage";
import { DemoDashboard } from './pages/dashboard/DemoDashboard.js';
import { ErgonomicsDashboard } from "./pages/dashboard/ErgonomicsDashboard";
import { ConstructionDashboard } from "./pages/dashboard/ConstructionDashboard";
import { DiorDashboard } from "./pages/dashboard/DiorDashboard";
import { ExampleDashboard } from './pages/dashboard/ExampleDashboard.js';
import ScanTestPage from "./pages/tests/ScanTestPage";
import { LISIDashboard } from "./pages/dashboard/LISIDashboard";
import { VeralliaDashboard } from './pages/dashboard/VeralliaDashboard';
import { AnnotationPage } from "./pages/datasets/AnnotationPage";

export const App = () => {
  const location = useLocation();

  const mobilePages = ['/demo/stream', '/calibration', '/quality', '/audit', '/settings/devices'];
  const isTrialMobilePage = mobilePages.some((page) => location.pathname.includes(page));

  // if (isMobileDevice() && !isTrialMobilePage) return <ResponsiveFallbackComponent />;

  const GalleryPage = {
    'snapshots': SnapshotsGalleryPage,
    'event_scores': EventScoresGalleryPage
  }[process.env.REACT_APP_GALLERY_MODE]

  return (
    <Routes>
      <Route element={<CoreLayout />}>
        <Route path='/' element={<Navigate to={process.env.REACT_APP_DEFAULT_PAGE} replace />} />

        {/* Trial pages. */}
        <Route element={<TrialLayout />}>
          <Route path='/demo/setup' element={<TrialSetupPage />} />
          <Route path='/demo' element={<TrialHomePage />} />
          <Route path='/demo/stream/:desktopId' element={<TrialMobilePage />} />
          <Route path='/demo/live' element={<TrialLivePage />} />
        </Route>
        <Route path='/interface/home' element={<DemoHomePage />} />

        {/* Page requiring connection to the engine. */}
        <Route path='/journal' element={<JournalPage />} />

        <Route path='/register/:organization_token' element={<JournalPage />} />
        <Route path='/reset-password/:token' element={<ResetPasswordPage />} />

        {/* Setup pages. */}
        <Route path='/setup' element={<SetupPage />} />
        <Route path='/setup/:subtype' element={<SetupPage />} />
        <Route path='/specification/:deviceId' element={<SpecificationPage />} />

        {/* Dashboard pages. */}
        <Route path='/dashboard/*' element={<DynamicDashboard />} />
        <Route path='/dashboards/:clientName/*' element={<DynamicDashboard />} />

        {/* Commons pages. */}
        <Route path='/settings' element={<Outlet />}>
          <Route path='profile' element={<Profile />} />
          <Route path='devices/:deviceToken1/:deviceToken2' element={<DevicesSettingsPage />} />
        </Route>

        {/* Protected and sensitive pages */}
        <Route
          path='/backoffice'
          element={
            <ProtectedRoute requiredRole='ROLE_ROOT'>
              <SalesPage />
            </ProtectedRoute>
          }
        />

        {/* IHM operator pages */}
        <Route path='/operator' element={<OperatorPage />} />
        <Route path='/operator/:workstationId' element={<OperatorWorkstationPage />} />

        {/* Processes pages. */}
        <Route element={<ProcessLayout />}>
          <Route path='/processes' element={<ProcessesPage />} />
          <Route path='/processes/new' element={<NewProcessPage />} />
          <Route path='/processes/:patternId' element={<ProcessPage />} />

          {/* Operator pages (V2) */}
          <Route path='/interface/live' element={<DynamicLivePage />} />
          <Route path='/interface/live/:screen' element={<DynamicLivePage />} />
          <Route path='/interface/setup' element={<DynamicLivePage isSetup={true} />} />
          <Route path='/interface/setup/:screen' element={<DynamicLivePage isSetup={true} />} />
          <Route path='/interfaces/:clientName/live' element={<DynamicLivePage />} />
          <Route path='/interfaces/:clientName/live/:screen' element={<DynamicLivePage />} />
          <Route path='/interfaces/:clientName/setup' element={<DynamicLivePage isSetup={true} />} />
        </Route>

        {/* Quality control pages */}
        <Route path='/gallery' element={<GalleryPage showFilters={true} showUsedSpaceMode="count" showScores={true} />} />
        <Route path='/recorder-gallery' element={<GalleryPage showUsedSpaceMode="disk" showScores={false} />} />

        {/* Shorts */}
        {/*<Route path='/shorts' element={<EventScoresGalleryPage showFilters={false} showUsedSpaceMode="disk" showScores={false} shortsMode={true} />} />*/}
        <Route path='/shorts' element={<ShortsPage />} />

        {/* Audit control pages */}
        <Route path='/audit' element={<AuditPage />} />
        <Route path='/audit/summary' element={<AuditSummaryPage />} />

        {/* Ergonomics pages */}
        <Route path='/ergonomics' element={<ErgonomicsPage />} />
        <Route path='/ergonomics/live' element={<ErgonomicsLivePage />} />

        {/* Camera calibration page */}
        <Route path='/calibration/:deviceToken1/:deviceToken2' element={<CalibrationPage />} />

        {/* Quality control page */}
        <Route path='/quality' element={<QualityPage />} />

        {/* Recorder kit pages */}
        <Route path='/recorder' element={<RecorderPage />} />
        <Route path='/recorder/meet' element={<RecorderMeetPage />} />

        {/* Dataset annotation */}
        <Route path='/datasets/annotate' element={<AnnotationPage />} />

        {/* Test pages */}
        <Route path='/tests/scan' element={<ScanTestPage />} />

        {/* Default redirection */}
        <Route path='*' element={<Navigate to='/' replace />} />
      </Route>
    </Routes>
  );
};

const DynamicDashboard = () => {
  const { clientName } = useParams();
  const resolvedDashboardName = clientName ?? process.env.REACT_APP_DASHBOARD_NAME;

  const DashboardPage = {
    'Demo': DemoDashboard,
    'Ergonomics': ErgonomicsDashboard,
    'Dior': DiorDashboard,
    'Construction': ConstructionDashboard,
    'LISI': LISIDashboard,
    'Verallia': VeralliaDashboard,
    'Example': ExampleDashboard,
  }[resolvedDashboardName];

  if (!DashboardPage) return <Navigate to='/' replace />;

  return <DashboardPage />;
};

const DynamicLivePage = ({ isSetup = false }) => {
  const { clientName } = useParams();
  const resolvedClientName = clientName ?? process.env.REACT_APP_CUSTOMER_NAME;
  const LivePage = {
    'EDF': EDFLivePage,
    'Hermes': HermesLivePage,
    'HermesUS': HermesUSLivePage,
    'LCA': LCALivePage,
    'LISI': LISILivePage,
    'Renault': RenaultLivePage,
    'TAG': TAGLivePage,
    'TAG-US': TAGUSLivePage,
    'Dior': DiorLivePage,
    'Arquus': ArquusLivePage,
    'Demo': DemoLivePage,
  }[resolvedClientName];

  if (!LivePage) return <Navigate to='/' replace />;

  return <LivePage isSetup={isSetup} />;
};

const ResponsiveFallbackComponent = () => {
  return (
    <div className='fixed top-0 left-0 flex flex-col items-center justify-center w-full h-full px-4 md:flex-row'>
      <img src={Issue} className='w-2/3 h-auto md:w-2/4' alt='issue, this website is not optimized for mobile' />
      <div className='max-w-md p-6 text-center bg-white rounded-lg md:text-left'>
        <h1 className='mb-2 text-2xl font-bold'>Oops!</h1>
        <p className='mb-2'>This site is not optimized for mobile devices.</p>
        <p>Please open it on a desktop or laptop computer for a better experience.</p>
        <div className='mt-4'>
          <a
            href='https://www.linkedin.com/company/perception-manufacturing'
            rel='noopener noreferrer'
            className='px-4 py-2 font-bold text-white rounded bg-perception-blue hover:bg-blue-700'
          >
            Contact us
          </a>
        </div>
      </div>
    </div>
  );
};
